import React from 'react';
import { FORM_BUILDER_DEFAULT_OPTIONS, FORM_BUILDER_OPTION_BLOCK_TYPES } from '../../forms/formBuilder/optionBlocks';
import { useBlockLibrary, useLibraryBlock } from '../../forms/formBuilder/blockLibraryContext';
import {
  listPatientFieldTargets,
  unsupportedChoiceOptions,
  type PatientFieldTarget,
} from '../../forms/formBuilder/patientFieldTargets';
import { resolveFormBuilderBlockIcon } from '../../forms/formBuilder/blockIcons';
import type { FormBuilderBlockIconKey } from '../../forms/formBuilder/blockIconTypes';
import type { BlockCondition, CanvasBlock, FieldOverride } from '../../forms/types';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { FormBuilderBlockIcon } from './FormBuilderBlockIcons';
import { describeConditionAction } from './FormBuilderConditionModal';

export interface FormBuilderRightPanelProps {
  selectedBlock: CanvasBlock | null;
  formName: string;
  blockCount: number;
  updateBlock: (uid: string, patch: Partial<CanvasBlock>) => void;
  onAddCondition: (block: CanvasBlock) => void;
  onRemoveCondition?: (uid: string) => void;
  onOpenFormSettings?: () => void;
}

export function FormBuilderRightPanel({
  selectedBlock,
  formName,
  blockCount,
  updateBlock,
  onAddCondition,
  onRemoveCondition,
  onOpenFormSettings,
}: FormBuilderRightPanelProps) {
  if (!selectedBlock) {
    return (
      <RightEmpty
        formName={formName}
        blockCount={blockCount}
        {...(onOpenFormSettings ? { onOpenFormSettings } : {})}
      />
    );
  }
  const removeCond = onRemoveCondition
    ? () => {
        onRemoveCondition(selectedBlock.uid);
      }
    : undefined;

  if (selectedBlock.source === 'basic') {
    return (
      <RightBasicProps
        block={selectedBlock}
        update={(patch) => {
          updateBlock(selectedBlock.uid, patch);
        }}
        onAddCondition={() => {
          onAddCondition(selectedBlock);
        }}
        {...(removeCond ? { onRemoveCondition: removeCond } : {})}
      />
    );
  }
  if (selectedBlock.source === 'patient') {
    return (
      <RightPatientProps
        block={selectedBlock}
        update={(patch) => {
          updateBlock(selectedBlock.uid, patch);
        }}
        onAddCondition={() => {
          onAddCondition(selectedBlock);
        }}
        {...(removeCond ? { onRemoveCondition: removeCond } : {})}
      />
    );
  }
  return (
    <RightSmartProps
      block={selectedBlock}
      update={(patch) => {
        updateBlock(selectedBlock.uid, patch);
      }}
      onAddCondition={() => {
        onAddCondition(selectedBlock);
      }}
      {...(removeCond ? { onRemoveCondition: removeCond } : {})}
    />
  );
}

function RightEmpty({
  formName,
  blockCount,
  onOpenFormSettings,
}: {
  formName: string;
  blockCount: number;
  onOpenFormSettings?: () => void;
}) {
  return (
    <aside className="fm-builder-panel flex shrink-0 flex-col border-l px-[22px] py-5">
      <p className="mb-2.5 font-[var(--font-mono)] text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-3)]">
        Form overview
      </p>
      <h2 className="mb-1 font-[var(--font-display)] text-base font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
        {formName}
      </h2>
      <p className="mb-5 font-[var(--font-mono)] text-xs text-[var(--ink-3)]">
        {blockCount === 0 ? 'No blocks yet · Draft' : `${blockCount} blocks · Draft`}
      </p>
      <div className="mb-5 rounded-[11px] border border-[color-mix(in_srgb,var(--ai-3)_25%,var(--cream-2))] bg-[var(--ai-tint)] px-4 py-4">
        <p className="mb-3 text-xs leading-relaxed text-[var(--ink-2)]">
          {blockCount === 0
            ? 'Start by adding a block from the library — drag it onto the canvas or click to insert.'
            : 'Click any block in the canvas to edit its properties, customize fields, or add conditional logic.'}
        </p>
        <Button
          variant="secondary"
          size="sm"
          iconLeft="cog"
          className="w-full border-[color-mix(in_srgb,var(--ai-3)_30%,var(--cream-2))] !text-[var(--ai-3)]"
          onClick={onOpenFormSettings}
        >
          Form settings
        </Button>
      </div>
      <div className="text-[11px] leading-relaxed text-[var(--ink-4)]">
        <p className="mb-1.5 font-semibold text-[var(--ink-3)]">TIPS</p>
        <p className="mb-2">• Drag blocks from the library or click to append.</p>
        <p className="mb-2">• Drag blocks within the canvas to reorder.</p>
        <p>• Smart blocks let you toggle individual fields on/off.</p>
      </div>
    </aside>
  );
}

function RightShell({
  title,
  subtitle,
  badge,
  accentColor,
  icon,
  children,
}: {
  title: string;
  subtitle: string;
  badge?: { label: string; gradient?: boolean; color?: string; compliance?: boolean };
  accentColor?: string;
  icon?: FormBuilderBlockIconKey;
  children: React.ReactNode;
}) {
  return (
    <aside className="fm-builder-panel flex shrink-0 flex-col border-l">
      <header className="border-b border-[var(--cream-2)] px-[22px] py-4">
        <p className="mb-2 font-[var(--font-mono)] text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-3)]">
          {subtitle}
        </p>
        <div className="flex items-center gap-2.5">
          {icon && accentColor ? (
            <div
              className="flex size-8 shrink-0 items-center justify-center rounded-lg"
              style={{
                backgroundColor: `color-mix(in srgb, ${accentColor} 12%, var(--cream-1))`,
                color: accentColor,
              }}
            >
              <FormBuilderBlockIcon icon={icon} size={14} />
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[14.5px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">{title}</h2>
              {badge?.gradient ? (
                <span className="fm-badge-smart">
                  <Icon kind="sparkle" size={8} />
                  {badge.label}
                </span>
              ) : badge ? (
                <span className="fm-badge-compliance">
                  {badge.compliance ? <Icon kind="shield" size={8} /> : null}
                  {badge.label}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </header>
      <div className="cal-scroll flex-1 overflow-y-auto overscroll-contain px-[22px] py-5">{children}</div>
    </aside>
  );
}

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">{label}</p>
      {children}
      {hint ? <p className="mt-1 text-[11px] leading-snug text-[var(--ink-4)]">{hint}</p> : null}
    </div>
  );
}

function BuilderInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'w-full rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-2 text-[12.5px] text-[var(--ink-0)] outline-none focus:border-[var(--ai-3)] focus:shadow-[0_0_0_3px_var(--ai-tint)]',
        props.className,
      )}
    />
  );
}

function BuilderTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        'w-full resize-y rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-2 text-[12.5px] text-[var(--ink-0)] outline-none focus:border-[var(--ai-3)] focus:shadow-[0_0_0_3px_var(--ai-tint)]',
        props.className,
      )}
    />
  );
}

function ToggleRow({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--cream-2)] py-2.5">
      <span className="text-[12.5px] font-medium text-[var(--ink-1)]">{label}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) onChange(!value);
        }}
        className={cn('fm-builder-toggle', value ? 'is-on' : 'is-off', disabled && 'opacity-50')}
        aria-pressed={value}
      >
        <span className="fm-builder-toggle-knob" />
      </button>
    </div>
  );
}

function ConditionSection({
  block,
  onAddCondition,
  onRemoveCondition = undefined,
}: {
  block: CanvasBlock;
  onAddCondition: () => void;
  onRemoveCondition?: (() => void) | undefined;
}) {
  if (block.condition) {
    const c = block.condition;
    return (
      <FieldRow label="Conditional logic">
        <div className="rounded-[9px] border border-[color-mix(in_srgb,var(--gold)_30%,var(--cream-2))] bg-[var(--gold-tint)] px-3 py-2.5">
          <p className="mb-2 text-[11.5px] leading-relaxed text-[var(--ink-2)]">
            {describeConditionAction(c.action)} this when <strong>{c.fieldLabel}</strong> {c.operator}{' '}
            {c.value && !['is empty', 'is not empty'].includes(c.operator) ? (
              <>
                &ldquo;<strong>{c.value}</strong>&rdquo;
              </>
            ) : null}
          </p>
          <div className="flex gap-1.5">
            <Button variant="secondary" size="sm" className="flex-1" onClick={onAddCondition}>
              Edit
            </Button>
            {onRemoveCondition ? (
              <Button variant="secondary" size="sm" className="!text-[var(--terracotta)]" onClick={onRemoveCondition}>
                Remove
              </Button>
            ) : null}
          </div>
        </div>
      </FieldRow>
    );
  }
  return (
    <button
      type="button"
      onClick={onAddCondition}
      className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-[9px] border-[1.5px] border-dashed border-[var(--cream-2)] bg-transparent px-3 py-2.5 text-[12.5px] font-medium text-[var(--ink-3)] transition-colors hover:border-[var(--gold)] hover:bg-[var(--gold-tint)] hover:text-[var(--gold)]"
    >
      <Icon kind="bolt" size={12} />
      Add condition
    </button>
  );
}

const SELECT_CLASS =
  'w-full rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-2 text-[12.5px] text-[var(--ink-0)] outline-none focus:border-[var(--ai-3)]';

/** Optional write-back target for a basic field — the answer updates this patient field on approve. */
function PatientFieldTargetRow({
  block,
  update,
}: {
  block: CanvasBlock;
  update: (patch: Partial<CanvasBlock>) => void;
}) {
  const library = useBlockLibrary();
  const targets = React.useMemo(() => listPatientFieldTargets(library, block.typeId), [library, block.typeId]);
  const groups = React.useMemo(() => {
    const byGroup = new Map<string, PatientFieldTarget[]>();
    for (const target of targets) byGroup.set(target.group, [...(byGroup.get(target.group) ?? []), target]);
    return [...byGroup.entries()];
  }, [targets]);
  const cfg = block.config ?? {};
  const current = cfg.fieldPath ?? '';
  if (targets.length === 0 && !current) return null;

  const selected = targets.find((target) => target.path === current);
  const options = cfg.options ?? FORM_BUILDER_DEFAULT_OPTIONS[block.typeId] ?? [];
  const unsupported = selected ? unsupportedChoiceOptions(options, selected) : [];

  return (
    <FieldRow label="Updates patient field" hint="Optional. On approve, the answer is written to this field.">
      <select
        value={current}
        onChange={(e) => {
          const { fieldPath: _drop, ...rest } = cfg;
          update({ config: e.target.value ? { ...rest, fieldPath: e.target.value } : rest });
        }}
        className={SELECT_CLASS}
      >
        <option value="">Don&apos;t update</option>
        {current && !selected ? <option value={current}>{current}</option> : null}
        {groups.map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map((target) => (
              <option key={target.path} value={target.path}>
                {target.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {current ? (
        <p className="mt-1.5 font-[var(--font-mono)] text-[11px] text-[var(--info)]">{current}</p>
      ) : null}
      {selected?.options && unsupported.length > 0 ? (
        <div className="mt-2 rounded-[9px] border border-[color-mix(in_srgb,var(--gold)_30%,var(--cream-2))] bg-[var(--gold-tint)] px-3 py-2.5">
          <p className="mb-2 text-[11.5px] leading-relaxed text-[var(--ink-2)]">
            Not accepted by the PMS for {selected.label}: <strong>{unsupported.join(', ')}</strong>. Accepted:{' '}
            {selected.options.join(', ')}.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={() => {
              update({ config: { ...cfg, options: [...(selected.options ?? [])] } });
            }}
          >
            Use PMS options
          </Button>
        </div>
      ) : null}
    </FieldRow>
  );
}

function RightBasicProps({
  block,
  update,
  onAddCondition,
  onRemoveCondition = undefined,
}: {
  block: CanvasBlock;
  update: (patch: Partial<CanvasBlock>) => void;
  onAddCondition: () => void;
  onRemoveCondition?: (() => void) | undefined;
}) {
  const def = useLibraryBlock(block.typeId);
  const cfg = block.config ?? {};
  const supportsOptions = FORM_BUILDER_OPTION_BLOCK_TYPES.has(block.typeId);
  const isSingleCheckbox = block.typeId === 'checkbox';
  const options = cfg.options ?? FORM_BUILDER_DEFAULT_OPTIONS[block.typeId] ?? [];
  const configKeys = new Set(def?.configKeys ?? []);
  const isChoiceGroup = block.typeId === 'radio' || block.typeId === 'checkbox_group';

  const setConfig = (patch: Partial<NonNullable<CanvasBlock['config']>>) => {
    update({ config: { ...cfg, ...patch } });
  };

  return (
    <RightShell
      title={def?.label ?? 'Block'}
      subtitle="Basic block"
      accentColor="var(--ink-3)"
      icon={def ? resolveFormBuilderBlockIcon(def) : 'FileText'}
    >
      <FieldRow label="Label">
        <BuilderInput
          value={cfg.label ?? ''}
          onChange={(e) => {
            setConfig({ label: e.target.value });
          }}
        />
      </FieldRow>
      <PatientFieldTargetRow block={block} update={update} />
      {supportsOptions ? (
        isSingleCheckbox ? (
          <FieldRow label="Checkbox text" hint="Text shown next to the checkbox.">
            <BuilderInput
              value={options[0] ?? ''}
              placeholder="Check this box"
              onChange={(e) => {
                setConfig({ options: [e.target.value] });
              }}
            />
          </FieldRow>
        ) : (
          <FieldRow label="Options" hint="Choices patients can select from.">
            <BlockOptionsEditor
              options={options}
              onChange={(next) => {
                setConfig({ options: next });
              }}
            />
          </FieldRow>
        )
      ) : (
        <FieldRow label="Placeholder">
          <BuilderInput
            value={cfg.placeholder ?? ''}
            placeholder="Optional…"
            onChange={(e) => {
              setConfig({ placeholder: e.target.value });
            }}
          />
        </FieldRow>
      )}
      <FieldRow label="Help text">
        <BuilderInput
          value={cfg.helpText ?? ''}
          placeholder="Optional…"
          onChange={(e) => {
            setConfig({ helpText: e.target.value });
          }}
        />
      </FieldRow>
      {isChoiceGroup && configKeys.has('allow_other') ? (
        <ToggleRow
          label="Add “Other” with text box"
          value={cfg.allowOther ?? false}
          onChange={(v) => {
            setConfig({ allowOther: v });
          }}
        />
      ) : null}
      {isChoiceGroup && configKeys.has('columns') ? (
        <FieldRow label="Columns" hint="How many columns the options use on wider screens.">
          <select
            value={cfg.columns ?? 1}
            onChange={(e) => {
              setConfig({ columns: Number(e.target.value) });
            }}
            className={SELECT_CLASS}
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </FieldRow>
      ) : null}
      {block.typeId === 'text_input' && configKeys.has('input_format') ? (
        <ToggleRow
          label="Full SSN (###-##-####)"
          value={cfg.inputFormat === 'ssn'}
          onChange={(v) => {
            const { inputFormat: _drop, ...rest } = cfg;
            update({ config: v ? { ...rest, inputFormat: 'ssn' } : rest });
          }}
        />
      ) : null}
      {block.typeId === 'file_upload' && configKeys.has('accept') ? (
        <FieldRow label="Accepted file types" hint="Comma-separated, e.g. .png,.jpg,.jpeg,.pdf">
          <BuilderInput
            value={cfg.accept ?? ''}
            placeholder="Any file"
            onChange={(e) => {
              setConfig({ accept: e.target.value });
            }}
          />
        </FieldRow>
      ) : null}
      <ToggleRow
        label="Required"
        value={cfg.required ?? false}
        onChange={(v) => {
          setConfig({ required: v });
        }}
      />
      <ConditionSection block={block} onAddCondition={onAddCondition} onRemoveCondition={onRemoveCondition} />
    </RightShell>
  );
}

function RightPatientProps({
  block,
  update,
  onAddCondition,
  onRemoveCondition = undefined,
}: {
  block: CanvasBlock;
  update: (patch: Partial<CanvasBlock>) => void;
  onAddCondition: () => void;
  onRemoveCondition?: (() => void) | undefined;
}) {
  const def = useLibraryBlock(block.typeId);
  return (
    <RightShell
      title={def?.label ?? 'Patient field'}
      subtitle="Patient Info"
      badge={{ label: 'PMS', color: 'var(--info)' }}
      accentColor="var(--info)"
      icon={def ? resolveFormBuilderBlockIcon(def) : 'Type'}
    >
      <div className="mb-4 flex gap-2 rounded-[9px] border border-[color-mix(in_srgb,var(--info)_20%,var(--cream-2))] bg-[color-mix(in_srgb,var(--info)_8%,var(--cream-1))] px-3 py-2.5">
        <Icon kind="team" size={14} className="mt-0.5 shrink-0 text-[var(--info)]" />
        <p className="text-[11.5px] leading-relaxed text-[var(--ink-2)]">
          Syncs to <code className="font-[var(--font-mono)] text-[11px] text-[var(--info)]">{def?.desc}</code>
        </p>
      </div>
      <FieldRow label="Custom label" hint="Override the default label patients see.">
        <BuilderInput
          value={block.customLabel ?? ''}
          placeholder={def?.label}
          onChange={(e) => {
            update({ customLabel: e.target.value });
          }}
        />
      </FieldRow>
      <ToggleRow
        label="Required"
        value={block.required !== false}
        onChange={(v) => {
          update({ required: v });
        }}
      />
      <ConditionSection block={block} onAddCondition={onAddCondition} onRemoveCondition={onRemoveCondition} />
    </RightShell>
  );
}

function RightSmartProps({
  block,
  update,
  onAddCondition,
  onRemoveCondition = undefined,
}: {
  block: CanvasBlock;
  update: (patch: Partial<CanvasBlock>) => void;
  onAddCondition: () => void;
  onRemoveCondition?: (() => void) | undefined;
}) {
  const tpl = useLibraryBlock(block.typeId);
  if (!tpl) return null;
  const isCompliance = tpl.compliance;

  return (
    <RightShell
      title={tpl.label}
      subtitle={tpl.category ?? 'Smart block'}
      badge={
        isCompliance
          ? { label: 'Compliance', compliance: true }
          : { label: 'Smart', gradient: true }
      }
      accentColor={tpl.accentColor ?? 'var(--ai-3)'}
      icon={resolveFormBuilderBlockIcon(tpl)}
    >
      {isCompliance ? (
        <div className="mb-5 flex gap-2 rounded-[10px] border border-[color-mix(in_srgb,var(--gold)_30%,var(--cream-2))] bg-[var(--gold-tint)] px-3.5 py-3">
          <Icon kind="lock" size={14} className="mt-0.5 shrink-0 text-[var(--gold)]" />
          <p className="text-[11.5px] leading-relaxed text-[var(--ink-2)]">
            This is a <strong>Compliance Pack</strong> block. Legal text is locked and AI editing is disabled. You can
            add custom intro / outro text below.
          </p>
        </div>
      ) : null}

      {tpl.fields?.length ? (
        <div className="mb-5">
          <p className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">
            Fields in this block{' '}
            <span className="font-[var(--font-mono)] text-[var(--ink-4)]">{tpl.fields.length}</span>
          </p>
          <div className="flex flex-col gap-0.5">
            {tpl.fields.map((field) => (
              <FieldCustomizationRow
                key={field.id}
                field={field}
                override={block.overrides?.[field.id] ?? {}}
                onChange={(patch) => {
                  const overrides: Record<string, FieldOverride> = {
                    ...(block.overrides ?? {}),
                    [field.id]: { ...(block.overrides?.[field.id] ?? {}), ...patch },
                  };
                  update({ overrides });
                }}
              />
            ))}
          </div>
        </div>
      ) : null}

      {tpl.hasConsent && !isCompliance ? (
        <FieldRow label="AI-assisted edit" hint="Describe how you'd like the consent text adjusted.">
          <div className="relative">
            <BuilderTextarea
              rows={3}
              placeholder="e.g. Make this also cover orthodontic treatment…"
              className="min-h-[70px] pr-10"
            />
            <button
              type="button"
              title="Generate"
              className="absolute bottom-2 right-2 flex size-7 items-center justify-center rounded-[7px] bg-[var(--ai-3)] text-white"
            >
              <Icon kind="sparkle" size={12} />
            </button>
          </div>
        </FieldRow>
      ) : null}

      <FieldRow
        label="Custom intro text"
        hint={isCompliance ? 'Shown ABOVE the locked legal text.' : 'Optional preamble.'}
      >
        <BuilderTextarea
          rows={2}
          value={block.customIntro ?? ''}
          placeholder="Optional…"
          onChange={(e) => {
            update({ customIntro: e.target.value });
          }}
        />
      </FieldRow>

      {isCompliance ? (
        <FieldRow label="Custom outro text" hint="Shown BELOW the locked legal text.">
          <BuilderTextarea
            rows={2}
            value={block.customOutro ?? ''}
            placeholder="Optional…"
            onChange={(e) => {
              update({ customOutro: e.target.value });
            }}
          />
        </FieldRow>
      ) : null}

      <ConditionSection block={block} onAddCondition={onAddCondition} onRemoveCondition={onRemoveCondition} />
    </RightShell>
  );
}

function BlockOptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (options: string[]) => void;
}) {
  const updateOption = (index: number, value: string) => {
    const next = [...options];
    next[index] = value;
    onChange(next);
  };

  const removeOption = (index: number) => {
    if (options.length <= 1) return;
    onChange(options.filter((_, i) => i !== index));
  };

  const addOption = () => {
    onChange([...options, `Option ${options.length + 1}`]);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {options.map((opt, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <BuilderInput
            value={opt}
            placeholder={`Option ${index + 1}`}
            onChange={(e) => {
              updateOption(index, e.target.value);
            }}
          />
          <button
            type="button"
            title="Remove option"
            disabled={options.length <= 1}
            onClick={() => {
              removeOption(index);
            }}
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-3)] transition-colors hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]',
              options.length <= 1 && 'cursor-not-allowed opacity-40',
            )}
          >
            <Icon kind="trash" size={12} />
          </button>
        </div>
      ))}
      <Button variant="secondary" size="sm" iconLeft="plus" className="w-full" onClick={addOption}>
        Add option
      </Button>
    </div>
  );
}

/**
 * W16 / D41⑤ — authoring-time mapping availability.
 * Wording is deliberate: "Structured mapping available" / "Manual entry" — never
 * "PMS mapping" and never "syncs". `mapping_available` means the answer lands
 * structured in ELVA's record; whether it reaches the PMS is D17's per-clinic
 * `will_sync` decision at review time, not a build-time promise.
 */
function FieldMappingBadge({ available }: { available: boolean }) {
  if (available) {
    return (
      <span
        title="Structured mapping available"
        className="shrink-0 rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-[var(--info)]"
        style={{ background: 'color-mix(in srgb, var(--info) 12%, var(--cream-1))' }}
      >
        Structured
      </span>
    );
  }
  return (
    <span
      title="Manual entry"
      className="shrink-0 rounded bg-[var(--cream-0)] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-[var(--ink-4)]"
    >
      Manual
    </span>
  );
}

function FieldCustomizationRow({
  field,
  override,
  onChange,
}: {
  field: { id: string; label: string; locked?: boolean; required?: boolean; mappingAvailable?: boolean };
  override: FieldOverride;
  onChange: (patch: FieldOverride) => void;
}) {
  const visible = override.visible !== false;
  const required = override.required ?? field.required ?? false;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg px-2.5 py-2',
        !visible && 'bg-[var(--cream-0)] opacity-55',
      )}
    >
      <button
        type="button"
        disabled={field.locked}
        title={field.locked ? 'Required field — cannot be hidden' : visible ? 'Hide field' : 'Show field'}
        onClick={() => {
          if (!field.locked) onChange({ visible: !visible });
        }}
        className={cn(
          'flex size-[18px] shrink-0 items-center justify-center rounded border-[1.5px]',
          visible ? 'border-[var(--ai-3)] bg-[var(--ai-3)] text-white' : 'border-[var(--cream-shadow)] bg-[var(--cream-1)]',
          field.locked && 'cursor-not-allowed',
        )}
      >
        {visible ? <Icon kind="check" size={10} stroke={3} /> : null}
      </button>
      <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-[var(--ink-2)]">{field.label}</span>
      <FieldMappingBadge available={field.mappingAvailable === true} />
      {field.locked ? <Icon kind="lock" size={10} className="text-[var(--ink-4)]" /> : null}
      <button
        type="button"
        disabled={field.locked}
        onClick={() => {
          if (!field.locked) onChange({ required: !required });
        }}
        className={cn(
          'shrink-0 rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide',
          required ? 'bg-[var(--terracotta-tint)] text-[var(--terracotta)]' : 'bg-[var(--cream-0)] text-[var(--ink-3)]',
          field.locked && 'cursor-not-allowed',
        )}
      >
        {required ? 'Required' : 'Optional'}
      </button>
    </div>
  );
}
